import { S25Const } from "../../util/s25-const";
import { Item } from "../../pojo/Item";
import { OptUtil } from "../s25-opt/s25.opt.util";
import { S25Util } from "../../util/s25-util";
import { ContactService } from "../../services/contact.service";
import { FlsService } from "../../services/fls.service";
import { UserprefService } from "../../services/userpref.service";
import { PreferenceService } from "../../services/preference.service";
import { CacheRepository } from "../../decorators/cache.decorator";
import { hasFullFls } from "../../pojo/Fls";
import { S25Help } from "../s25-help/s25.help.service";
import { ContactI } from "../../pojo/ContactI";
import { CustomAttributeService } from "../../services/custom.attribute.service";
import { Debounce } from "../../decorators/debounce.decorator";
import { Contact } from "../../pojo/Contact";
import { S25ObjectDetailsGeneralComponent } from "./general-details/s25.object.details.general.component";
import { S25ContactDetailsUserAccountComponent } from "./contact-details/user-account/s25.contact.details.user.account";
import { S25ObjectDetailsCommentsComponent } from "./s25.object.details.comments.component";
import { S25EditableContactOrgsComponent } from "./contact-details/editable-orgs/s25.editable.contact.orgs.component";
import { S25MasqueradeRolesComponent } from "../S25-masquerade/masquerade.roles.component";
import { S25ObjectDetailsAddressComponent } from "./address/s25.object.details.address.component";
import { Proto } from "../../pojo/Proto";
import NumericalBoolean = Proto.NumericalBoolean;
import { S25ObjectDetailsCustomAttrComponent } from "./custom-attrs/s25.object.details.custom.attr.component";

export class S25ObjectDetailsService {
    public static async getObjDetailsData(itemId: number, itemTypeId: Item.Id) {
        switch (itemTypeId) {
            case Item.Ids.Contact:
                return S25ObjectDetailsService.getContactDetailsData(itemId);
        }
    }

    private static getSearchQuery(itemId: number, itemTypeId: Item.Id): string {
        return `&${S25Const.typeId2ItemIdB[itemTypeId]}=${itemId}`;
    }

    @Debounce(500)
    private static async getContactDetailsData(itemId: number) {
        const [detailData, fls, dateFormat, groupId, editPref] = await Promise.all([
            ContactService.getContact(itemId),
            FlsService.getFls(),
            UserprefService.getS25Dateformat(),
            UserprefService.getGroupId(),
            PreferenceService.getPreferences(["ContShowInlineEdit"]),
        ]);
        const showInlineEdit = editPref?.ContShowInlineEdit?.value === "T",
            fullPerms = hasFullFls(fls.CU_CONTACT),
            contactDetails = detailData?.contacts?.contact ?? {},
            isAdmin = groupId === -1;
        let canEdit = fullPerms && showInlineEdit,
            canEditSecurity = hasFullFls(fls.SECURITY) && showInlineEdit,
            username,
            warningMessage;
        const { r25user } = contactDetails;
        if (r25user && canEdit) {
            const systemUsers = ["service25", "object25"];
            username = r25user?.r25_username;
            const isSystemUser = systemUsers.includes(username.toLowerCase());
            const isInternal = username.toLowerCase() === "viewer25";
            canEdit = canEdit && !isSystemUser && (isAdmin || !isInternal);
            canEditSecurity = canEditSecurity && canEdit && !isInternal;
            warningMessage = S25ObjectDetailsService.setContactWarningAlert(isAdmin, isSystemUser, isInternal);
        }
        const details = S25ObjectDetailsService.formatContactDetailsData(contactDetails);
        return {
            warningMessage: warningMessage,
            panels: [
                {
                    header: "General",
                    id: "general",
                    section: 0,
                    sectionOrder: 1,
                    content: {
                        component: S25ObjectDetailsGeneralComponent,
                        inputs: {
                            model: details.general,
                            itemTypeId: Item.Ids.Contact,
                            itemId: itemId,
                            canEdit: canEdit,
                        },
                    },
                },
                {
                    header: "User Account",
                    id: "account",
                    section: 0,
                    sectionOrder: 2,
                    content: {
                        component: S25ContactDetailsUserAccountComponent,
                        inputs: {
                            userData: details.userAccount,
                            canEdit: canEdit,
                            canEditSecurity: canEditSecurity,
                            itemId: itemId,
                            isAdmin: isAdmin,
                        },
                    },
                },
                {
                    header: "Comments",
                    id: "comments",
                    section: 0,
                    sectionOrder: 3,
                    content: {
                        component: S25ObjectDetailsCommentsComponent,
                        inputs: {
                            itemId: itemId,
                            itemTypeId: Item.Ids.Contact,
                            canEdit: canEdit,
                            comments: details.comments,
                        },
                    },
                },
                {
                    header: "Associated Organizations",
                    id: "assocOrgs",
                    section: 0,
                    sectionOrder: 4,
                    content: {
                        component: S25EditableContactOrgsComponent,
                        inputs: {
                            itemId: itemId,
                            canEdit: canEdit,
                            orgs: details.editableOrgs,
                        },
                    },
                },
                {
                    header: "Additional Roles",
                    id: "roles",
                    section: 1,
                    sectionOrder: 1,
                    content: {
                        component: S25MasqueradeRolesComponent,
                        inputs: {
                            itemId: itemId,
                            canEdit: canEdit,
                            groupId: groupId,
                        },
                    },
                },
                {
                    header: "Work Contact Details",
                    id: "workDetails",
                    section: 1,
                    sectionOrder: 2,
                    content: {
                        component: S25ObjectDetailsAddressComponent,
                        inputs: {
                            address: details.editableWorkAddress,
                            itemId: itemId,
                            itemTypeId: Item.Ids.Contact,
                            canEdit: canEdit,
                        },
                    },
                },
                {
                    header: "Home Contact Details",
                    id: "homeDetails",
                    section: 1,
                    sectionOrder: 3,
                    content: {
                        component: S25ObjectDetailsAddressComponent,
                        inputs: {
                            address: details.editableHomeAddress,
                            itemId: itemId,
                            itemTypeId: Item.Ids.Contact,
                            canEdit: canEdit,
                        },
                    },
                },
                {
                    header: "Attributes",
                    id: "attributes",
                    section: 1,
                    sectionOrder: 4,
                    content: {
                        component: S25ObjectDetailsCustomAttrComponent,
                        inputs: {
                            customAttrs: details.attributesEditable,
                            itemId: itemId,
                            itemTypeId: Item.Ids.Contact,
                            canEdit: canEdit,
                        },
                    },
                },
            ],
        };
    }

    private static setContactWarningAlert(isAdmin: boolean, isSystemUser: boolean, isInternalUser: boolean) {
        let message, link;
        if (isSystemUser) message = "This is a system defined contact record and cannot be edited.";
        else if (isInternalUser) {
            if (isAdmin) link = S25Help.getHelpUrl("system_users");
            const viewer25Warning = "Modifying the viewer25 account can have unintended consequences.";
            const adminOnly = "This is an internal contact, and can only be edited by an administrator.";
            message = isAdmin ? viewer25Warning : adminOnly;
        }
        if (message) return { message, link };
        return null;
    }

    private static formatContactDetailsData(contact: ContactI): Contact.DetailData {
        const {
            contact_name,
            first_name,
            last_name,
            middle_name,
            name_prefix,
            name_suffix,
            title,
            comments,
            internal_id,
            r25user,
            attribute,
            organization,
        } = contact;
        const attributes = S25Util.array.forceArray(attribute ?? []),
            organizations = S25Util.array.forceArray(organization ?? []),
            homeDetails = S25Util.propertyGetParentWithChildValue(contact, "address_type", "4"),
            workDetails = S25Util.propertyGetParentWithChildValue(contact, "address_type", "3");
        const {
            street_address: homeStreet,
            city: homeCity,
            state_prov: homeState,
            zip_post: homeZip,
            country: homeCountry,
            fax: homeFax,
            phone: homePhone,
            email: homeEmail,
        } = homeDetails || {};
        const {
            street_address: workStreet,
            city: workCity,
            state_prov: workState,
            zip_post: workZip,
            country: workCountry,
            fax: workFax,
            phone: workPhone,
            email: workEmail,
        } = workDetails || {};
        return {
            general: {
                contactName: contact_name,
                firstName: first_name,
                middleName: middle_name,
                lastName: last_name,
                namePrefix: name_prefix,
                nameSuffix: name_suffix,
                title: title,
                internalId: internal_id,
            },
            userAccount: {
                userName: r25user?.r25_username,
                active: r25user?.r25_defn_state as NumericalBoolean,
                securityGroup: {
                    itemName: r25user?.security_group?.security_group_name,
                    itemId: r25user?.security_group?.security_group_id,
                    itemTypeId: Item.Ids.Contact,
                },
            },
            comments: comments,
            attributesEditable: attributes.map((item) => CustomAttributeService.toCustAttrObj(item)),
            editableOrgs: organizations.map((org) => ({
                itemName: org.organization_name,
                itemId: org.organization_id,
                role: { itemId: org.role_id, itemName: org.role_name },
            })),
            editableHomeAddress: {
                street: homeStreet ?? "",
                city: homeCity ?? "",
                state: homeState ?? "",
                zip: homeZip ?? "",
                country: homeCountry ?? "",
                phone: homePhone ?? "",
                fax: homeFax ?? "",
                email: homeEmail ?? "",
                addressType: Contact.AddressType.Home,
            },
            editableWorkAddress: {
                street: workStreet ?? "",
                city: workCity ?? "",
                state: workState ?? "",
                zip: workZip ?? "",
                country: workCountry ?? "",
                phone: workPhone ?? "",
                fax: workFax ?? "",
                email: workEmail ?? "",
                addressType: Contact.AddressType.Work,
            },
        };
    }

    public static invalidateCache(itemTypeId: Item.Id) {
        return CacheRepository.invalidateByMethod(S25ObjectDetailsService.ObjMap[itemTypeId].invalidateMethod);
    }

    public static getOptBean(itemId: number, itemTypeId: Item.Id) {
        const optParams = {
            itemId: itemId,
            itemTypeId: itemTypeId,
            searchContext: S25ObjectDetailsService.getSearchQuery(itemId, itemTypeId),
        };
        return S25ObjectDetailsService.ObjMap[itemTypeId].getOptBean(null, optParams);
    }

    public static ObjMap: Partial<Record<Item.Id, ObjMapItem>> = {
        [Item.Ids.Contact]: {
            invalidateMethod: "ContactService.getContact",
            getOptBean: OptUtil.formContactDetailsBean,
        },
    };
}

type ObjMapItem = {
    invalidateMethod: string;
    getOptBean: (refreshF: any, runTimeParams: any) => Promise<any>;
};
